import React from 'react';
import { Box, Popover, Paper, useTheme } from '@mui/material';
import { format } from 'date-fns';
import Moon from './Moon';
import { findNextPhase, lunarPercent } from '../../utils/lunarphase';

type Props = {
  currentTime: Date;
  size?: number;
  style?: React.CSSProperties;
};

function useUpcomingPhases(now: Date) {
  return React.useMemo(() => {
    return [
      { key: 'New', frac: 0 },
      { key: 'First Quarter', frac: 0.25 },
      { key: 'Full', frac: 0.5 },
      { key: 'Last Quarter', frac: 0.75 },
    ].map(ph => ({ key: ph.key, when: findNextPhase(now, ph.frac) }));
  }, [now]);
}

type DiagramProps = {
  currentTime: Date;
  R?: number;
};

export const MoonPhaseDiagram: React.FC<DiagramProps> = ({ currentTime, R = 94 }) => {
  const theme = useTheme();
  const phases = useUpcomingPhases(currentTime);

  const cx = R + 16;
  const cy = R + 16;
  const pts = [
    { key: 'New', angle: -90, color: '#444' },
    { key: 'First Quarter', angle: 0, color: '#88c0d0' },
    { key: 'Full', angle: 90, color: '#ffd87e' },
    { key: 'Last Quarter', angle: 180, color: '#88c0d0' },
  ];

  return (
    <svg
      width={cx * 2}
      height={cy * 2}
      role="img"
      aria-label="Upcoming moon cycle"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <radialGradient id="ringGrad" cx="50%" cy="50%">
          <stop
            offset="0%"
            stopColor={
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.02)'
                : 'rgba(0,0,0,0.02)'
            }
          />
          <stop
            offset="100%"
            stopColor={
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.06)'
            }
          />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={R} fill="url(#ringGrad)" stroke={theme.palette.divider} />
      <text x={cx} y={cy} textAnchor="middle" fontSize={11} fill={theme.palette.text.secondary}>
        {currentTime ? format(currentTime, 'MMM do') : '—'}
      </text>
      {(() => {
        const lp = lunarPercent(currentTime); // 0..1
        const angle = lp * 360;
        return (
          <g aria-label="Current lunar position">
            <line
              x1={cx}
              y1={cy}
              x2={cx}
              y2={cy - R}
              stroke={theme.palette.primary.main}
              strokeWidth={1.2}
              transform={`rotate(${angle} ${cx} ${cy})`}
              style={{ opacity: 0.2 }}
            />
            <circle
              cx={0}
              cy={-R}
              r={2}
              fill={theme.palette.primary.main}
              transform={`translate(${cx} ${cy}) rotate(${angle})`}
              stroke="#fff"
              strokeWidth={1.2}
            />
          </g>
        );
      })()}
      {pts.map((p) => {
        const rad = (p.angle * Math.PI) / 180;
        const x = cx + R * Math.cos(rad);
        const y = cy + R * Math.sin(rad);
        const label = phases.find((ph) => ph.key === p.key)?.when;
        return (
          <g key={p.key}>
            <circle cx={x} cy={y} r={5} fill={p.color} />
            <text
              x={x}
              y={y - 8}
              textAnchor="middle"
              fontSize={11}
              fill={theme.palette.text.secondary}
            >
              {p.key}
            </text>
            <text
              x={x}
              y={y + 18}
              textAnchor="middle"
              fontSize={11}
              fill={theme.palette.text.primary}
            >
              {label ? format(label, 'PP') : '—'}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

function MoonWithPopup({ currentTime, size = 24, style }: Props) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Box
        ref={ref}
        sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setOpen(!open)}
        style={style}
      >
        <Moon currentTime={currentTime} size={size} />
      </Box>
      <Popover
        open={open}
        // keep it open when clicking elsewhere; allow page to scroll/interact
        disableScrollLock
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorEl={ref.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          root: { sx: { pointerEvents: 'none' } },
          paper: { sx: { p: 1.5, bgcolor: 'background.paper', pointerEvents: 'auto' } },
        }}
      >
        <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
          <MoonPhaseDiagram currentTime={currentTime} />
        </Paper>
      </Popover>
    </>
  );
}

export default React.memo(MoonWithPopup);
